import { useState, type CSSProperties } from "react";
import { useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  CONTACT_EMAIL,
  CONTACT_EMAIL_MAILTO,
  WHATSAPP_NUMBER_DISPLAY,
  WHATSAPP_NUMBER_E164,
} from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { panelStyle } from "../ui/card-frame";
import { StoreButtons } from "../ui/store-buttons";
import { NewsletterForm } from "../../features/newsletter";

// Top SEO hubs surfaced from every page via the footer. These are the same
// category/city slugs the sitemap exposes, picked for search demand and
// inventory depth so internal link equity flows to the highest-value
// landing pages on each render.
const FOOTER_CATEGORIES = [
  "activities",
  "excursions",
  "trips",
  "stays",
  "spas",
  "pools",
  "boats",
  "workshops",
] as const;

const FOOTER_CITIES = [
  "marrakech",
  "casablanca",
  "fez",
  "agadir",
  "essaouira",
  "chefchaouen",
  "rabat",
  "tangier",
] as const;

function titleize(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

const WHATSAPP_DIGITS = WHATSAPP_NUMBER_E164.replace(/\D/g, "");

// The same five accounts `shared/ui/social-links.tsx` draws on the contact
// page. Listed here rather than rendered through that component because the
// footer's discs are tinted, not outlined, and carry the house press motion,
// neither of which that component's anchor exposes. Keep the two in step.
const SOCIALS = [
  { name: "Facebook", href: "https://facebook.com/majourneys", icon: "ri-facebook-fill" },
  { name: "X", href: "https://x.com/majourneys", icon: "ri-twitter-x-fill" },
  { name: "Instagram", href: "https://instagram.com/majourneys.ma", icon: "ri-instagram-fill" },
  { name: "Pinterest", href: "https://pinterest.com/majourneys", icon: "ri-pinterest-fill" },
  { name: "TikTok", href: "https://tiktok.com/@majourneys", icon: "ri-tiktok-fill" },
];

/* The material, inline; the layout stays in classes. */
const NAVY = "#110E39";
const MUTED = "#5a5d77";
/** The rule between the panel's four bands. */
const HAIRLINE = "rgba(17,14,57,0.09)";

const BAND_RULE: CSSProperties = { borderTop: `1px solid ${HAIRLINE}` };

const GROUP_HEADING = "text-[11px] font-bold uppercase tracking-[0.08em] text-[#110E39]";

/** Colour lives in classes, not the style prop: hover cannot be written inline. */
const LINK_CLASSES =
  "text-[#5a5d77] transition-colors duration-[160ms] ease-[var(--mj-ease-out)] hover:text-[#110E39] focus-visible:text-[#110E39] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#110E39]";

type FooterLinkItem = { label: string; href: string; external?: boolean };

function externalProps(link: FooterLinkItem) {
  return link.external ? { target: "_blank", rel: "noopener noreferrer" } : {};
}

export function Footer() {
  const { t } = useTranslation();
  const { lang } = (useParams({ strict: false }) as unknown) as { lang?: string };
  const locale = lang ?? "en";
  const year = new Date().getFullYear();

  const categoryLinks = FOOTER_CATEGORIES.map((slug) => ({
    href: `/${locale}/category/${slug}`,
    label: t(`activity.category_${slug}`, { defaultValue: titleize(slug) }),
  }));

  const cityLinks = FOOTER_CITIES.map((slug) => ({
    href: `/${locale}/place/${slug}`,
    label: t(`nav.city.${slug}`, { defaultValue: titleize(slug) }),
  }));

  // Every link sits in exactly one group (owner, 2026-09-23): tools a guest
  // plans with, pages about us, and ways to reach us. The legal pages leave
  // the columns for the copyright line.
  const planLinks: FooterLinkItem[] = [
    { label: t("ai_concierge.title", { defaultValue: "MaJourneys AI" }), href: `/${locale}/ai-travel-planner` },
    { label: t("trip_planner_page.eyebrow", { defaultValue: "Trip planner" }), href: `/${locale}/trip-planner` },
    { label: t("nav.transport"), href: `/${locale}/transport` },
    { label: t("nav.blog", { defaultValue: "Blog" }), href: `/${locale}/blog` },
  ];

  const companyLinks: FooterLinkItem[] = [
    { label: t("nav.about", { defaultValue: "About" }), href: `/${locale}/about` },
    { label: t("nav.partner", { defaultValue: "Partner with us" }), href: `/${locale}/become-a-partner` },
    { label: t("nav.creators"), href: `/${locale}/creators` },
    { label: t("nav.ambassadors"), href: `/${locale}/ambassadors` },
  ];

  const legalLinks: FooterLinkItem[] = [
    { label: t("common.terms", { defaultValue: "Terms & Conditions" }), href: `/${locale}/terms-and-conditions` },
    { label: t("common.privacy", { defaultValue: "Privacy Policy" }), href: `/${locale}/privacy-policy` },
    { label: t("common.cookies", { defaultValue: "Cookie Policy" }), href: `/${locale}/cookie-policy` },
  ];

  const whatsappLink: FooterLinkItem = {
    label: t("footer.whatsapp_us", { defaultValue: "WhatsApp us" }),
    href: `https://wa.me/${WHATSAPP_DIGITS}`,
    external: true,
  };
  const emailLink: FooterLinkItem = {
    label: t("footer.email_us", { defaultValue: "Email us" }),
    href: CONTACT_EMAIL_MAILTO,
  };

  const supportLinks: FooterLinkItem[] = [
    whatsappLink,
    emailLink,
    { label: t("common.faqs", { defaultValue: "FAQs" }), href: `/${locale}/faq` },
    { label: t("nav.contact", { defaultValue: "Contact" }), href: `/${locale}/contact` },
  ];

  const groups = [
    { key: "categories", heading: t("footer.top_categories", { defaultValue: "Top categories" }), links: categoryLinks },
    { key: "cities", heading: t("footer.top_cities", { defaultValue: "Top cities" }), links: cityLinks },
    { key: "plan", heading: t("footer.plan_your_trip"), links: planLinks },
    { key: "company", heading: t("footer.company"), links: companyLinks },
    { key: "help", heading: t("footer.help"), links: supportLinks },
  ];

  return (
    <footer className="mj-site-footer mt-auto w-full">
      {/*
        A hairline where the page ends and the footer begins, on every page
        (owner, 2026-09-14).
        
        Without it the footer panel just appears after a stretch of page
        background, and how much background that is depends on whatever the
        last section happened to leave behind: the run-out reads as a gap on
        one page and as a missing section on the next. A line makes it a
        boundary rather than an accident, and the same one everywhere.

        Soft on purpose: navy at 8% is the lightest edge on the site that still
        registers. Anything heavier draws a rule across the page and competes
        with the panel right under it.
      */}
      <div className="mx-auto w-full max-w-[1700px] px-4 sm:px-6 lg:px-12 2xl:px-[120px]">
        <div className="h-px w-full bg-[rgba(17,14,57,0.08)]" />
      </div>
      {/* The old sheet's gutters. Below lg the MobileBottomBar floats over the
          page, so the extra bottom room keeps the copyright out from under it. */}
      <div className="mx-auto w-full max-w-[1700px] px-4 pb-6 pt-6 max-lg:pb-[calc(92px_+_env(safe-area-inset-bottom))] sm:px-6 lg:px-12 2xl:px-[120px]">
        {/* One sheet-toned panel, four bands, no overflow clip. */}
        <div className="w-full font-sans" style={panelStyle(24)}>
          {/* Band 1: brand and the newsletter */}
          <div className="grid gap-6 px-4 pb-5 pt-5 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] md:items-start md:gap-8 md:px-8 md:pb-6 md:pt-7">
            <div className="flex flex-col items-start gap-3">
              {/* The SVG's paths are already navy; no filter on a light panel. */}
              <img src="/images/majourneys-logo-light.svg" alt="MaJourneys" className="h-7 w-auto" />
              <p className="max-w-[34ch] text-[14px] leading-[1.5]" style={{ color: MUTED }}>
                {t("footer.description", {
                  defaultValue:
                    "Discover Morocco's best experiences, tours, and activities. Book your next adventure with MaJourneys.",
                })}
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <h2 className="text-[20px] font-semibold leading-[1.15] tracking-[-0.024em] md:text-[22px]" style={{ color: NAVY }}>
                {t("footer.newsletter_title", { defaultValue: "Stay Updated" })}
              </h2>
              <NewsletterForm />
            </div>
          </div>

          {/* Band 2: the link groups. Two layouts, both in the server HTML and
              switched by md: alone, so every hub is a real anchor at every
              width. Columns from md up; folds below, where a closed <details>
              keeps its list in the DOM. */}
          <div className="hidden gap-8 px-8 pb-7 pt-6 md:grid md:grid-cols-3 lg:grid-cols-5" style={BAND_RULE}>
            {groups.map((g) => (
              <div key={g.key}>
                <h2 className={`${GROUP_HEADING} mb-2.5`}>{g.heading}</h2>
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                  {g.links.map((link) => (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        {...externalProps(link)}
                        className={`inline-flex min-h-[24px] min-w-[24px] items-center text-[14px] leading-[1.4] ${LINK_CLASSES}`}
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <FooterAccordion groups={groups} />

          {/* Band 3: contact, then the socials */}
          <div
            className="flex flex-col gap-3 px-4 pb-4 pt-4 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4 md:px-8 md:py-5"
            style={BAND_RULE}
          >
            <div className="flex flex-col gap-3 md:gap-2">
              <div className="flex flex-wrap gap-2.5">
                <ContactButton link={whatsappLink} icon="ri-whatsapp-line" />
                <ContactButton link={emailLink} icon="ri-mail-line" />
              </div>
              {/* Both forced left-to-right so the dial code and the address read in order on the Arabic page. */}
              <div className="flex flex-col gap-0.5 text-[13px] md:flex-row md:flex-wrap md:items-center md:gap-x-2 md:text-[12.5px]">
                <a
                  href={`tel:${WHATSAPP_NUMBER_E164}`}
                  dir="ltr"
                  className="inline-flex min-h-[24px] w-fit items-center font-semibold tabular-nums"
                  style={{ color: NAVY }}
                >
                  {WHATSAPP_NUMBER_DISPLAY}
                </a>
                <span aria-hidden="true" className="hidden md:inline" style={{ color: MUTED }}>
                  ·
                </span>
                <a
                  href={CONTACT_EMAIL_MAILTO}
                  dir="ltr"
                  className="inline-flex min-h-[24px] w-fit items-center text-[#5a5d77] transition-colors duration-[160ms] ease-[var(--mj-ease-out)] hover:text-[#110E39]"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:justify-start">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  className="mj-press inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(17,14,57,0.055)] text-[#110E39] hover:bg-[#110E39] hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#110E39]"
                >
                  <Icon name={s.icon} size={16} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Band 4: store badges, the copyright and the legal pages */}
          <div
            className="flex flex-col items-center gap-3 px-4 pb-4 pt-3.5 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-4 md:px-8 md:pb-5 md:pt-4"
            style={BAND_RULE}
          >
            <div className="order-2 flex flex-col items-center gap-1.5 md:order-1 md:flex-row md:flex-wrap md:items-center md:gap-x-4">
              <p className="text-center text-[12px] tabular-nums md:text-start md:text-[12.5px]" style={{ color: MUTED }}>
                {t("footer.copyright", { year, defaultValue: "© {{year}} MaJourneys. All rights reserved." })}
              </p>
              <nav aria-label={t("footer.legal")}>
                <ul className="m-0 flex list-none flex-wrap items-center justify-center gap-x-3 p-0 text-[12px] md:text-[12.5px]">
                  {legalLinks.map((link) => (
                    <li key={link.href}>
                      <a href={link.href} className={`inline-flex min-h-[24px] items-center ${LINK_CLASSES}`}>
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            {/* Two 144px badges and the component's gap-5 come to 308px; at
                375px the panel leaves 303px. Tightened and allowed to wrap. */}
            <div className="order-1 md:order-2 [&>div]:flex-wrap [&>div]:justify-center [&>div]:gap-3">
              <StoreButtons />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * A hairline pill rather than a framed one: buttons and chips never wear the
 * pewter band (owner, 2026-09-05), the band is for surfaces you read. Rest and
 * hover shadows are both classes so the hover can win; an inline shadow would
 * beat it. Wide below md so the two share the row edge to edge.
 */
function ContactButton({ link, icon }: { link: FooterLinkItem; icon: string }) {
  return (
    <a
      href={link.href}
      {...externalProps(link)}
      className="mj-press flex flex-1 basis-[140px] items-center justify-center gap-2 rounded-full bg-[linear-gradient(180deg,#FFFFFF_0%,#F7F7FB_100%)] px-4 py-2 text-center text-[13px] font-semibold text-[#110E39] shadow-[0_0_0_1.5px_rgba(17,14,57,0.16)] hover:shadow-[0_0_0_1.5px_rgba(17,14,57,0.34)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#110E39] md:flex-none"
    >
      <Icon name={icon} size={14} className="shrink-0 text-[#5a5d77]" aria-hidden="true" />
      {link.label}
    </a>
  );
}

/**
 * The phone's link groups, one open at a time.
 *
 * Was a row of native <details>. They cannot animate shut on iOS, so every
 * tap snapped the page by a list's height, and a quick double tap selected
 * the heading and flashed the focus ring. Now each list is always in the DOM
 * (every hub stays a crawlable link) inside a 0fr/1fr grid row that eases
 * open and closed; the trigger takes no text selection and shows its ring to
 * the keyboard only.
 */
function FooterAccordion({ groups }: { groups: { key: string; heading: string; links: FooterLinkItem[] }[] }) {
  const [open, setOpen] = useState<string | null>(null);
  return (
    <div className="px-4 py-1 md:hidden" style={BAND_RULE}>
      {groups.map((g, i) => {
        const isOpen = open === g.key;
        const panelId = `footer-group-${g.key}`;
        return (
          <div key={g.key} style={i === 0 ? undefined : BAND_RULE}>
            <h2 className="m-0">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? null : g.key)}
                className="flex min-h-[48px] w-full select-none items-center justify-between gap-3 rounded-[10px] text-start outline-none [-webkit-tap-highlight-color:transparent] focus-visible:ring-2 focus-visible:ring-[#110E39]/40"
              >
                <span className={GROUP_HEADING}>{g.heading}</span>
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: isOpen ? "#110E39" : "rgba(17,14,57,0.055)",
                    color: isOpen ? "#fff" : "#5a5d77",
                    transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                    transition: "transform 300ms cubic-bezier(.2,.8,.2,1), background-color 200ms ease, color 200ms ease",
                  }}
                  aria-hidden="true"
                >
                  <Icon name="ri-add-line" className="text-[16px]" aria-hidden="true" />
                </span>
              </button>
            </h2>
            <div
              id={panelId}
              className="grid motion-reduce:transition-none"
              style={{
                gridTemplateRows: isOpen ? "1fr" : "0fr",
                transition: "grid-template-rows 320ms cubic-bezier(.2,.8,.2,1)",
              }}
            >
              <ul
                className="m-0 min-h-0 list-none overflow-hidden p-0"
                inert={!isOpen}
                style={{
                  opacity: isOpen ? 1 : 0,
                  transform: isOpen ? "translateY(0)" : "translateY(-6px)",
                  transition: "opacity 220ms ease, transform 320ms cubic-bezier(.2,.8,.2,1)",
                }}
              >
                {g.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      {...externalProps(link)}
                      className={`flex min-h-[40px] items-center text-[15px] ${LINK_CLASSES}`}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
                <li aria-hidden="true" className="h-3" />
              </ul>
            </div>
          </div>
        );
      })}
    </div>
  );
}
